import { fromEnumAsString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/dto.js';
import { EventBookingProtocol } from './event-booking-protocol.js';
import { EventBookingStrategy } from './event-booking-strategy.js';

export class VouchersOnlyEventBookingProtocol extends EventBookingProtocol {
  static readonly TYPE_ID: string = '4d15c87322704c45a3e7b30bcdf4a186';
  static readonly SCHEMA_VERSION: number = 3;

  public readonly strategy: EventBookingStrategy = EventBookingStrategy.Vouchers;

  private constructor(
    public readonly allowGenericOrders: boolean,
    public readonly shouldSendElectronicPass: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return VouchersOnlyEventBookingProtocol.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, VouchersOnlyEventBookingProtocol.SCHEMA_VERSION);
  }

  static create(args?: {
    allowGenericOrders?: boolean,
    shouldSendElectronicPass?: boolean,
  }): VouchersOnlyEventBookingProtocol {
    return new VouchersOnlyEventBookingProtocol(
        args?.allowGenericOrders ?? false,
        args?.shouldSendElectronicPass ?? true,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): VouchersOnlyEventBookingProtocol {
    return new VouchersOnlyEventBookingProtocol(
        MapUtils.getBoolean(map, 'allowGenericOrders'),
        MapUtils.getBoolean(map, 'shouldSendElectronicPass'),
    );
  }

  toMap(_context: SerializationContext): SerializedData {
    return {
      'strategy': fromEnumAsString(this.strategy),
      'allowGenericOrders': this.allowGenericOrders,
      'shouldSendElectronicPass': this.shouldSendElectronicPass,
    };
  }

  clone(): this {
    return new VouchersOnlyEventBookingProtocol(
        this.allowGenericOrders,
        this.shouldSendElectronicPass,
    ) as this;
  }

  isSame(other: VouchersOnlyEventBookingProtocol): boolean {
    if(this === other) {
      return true;
    }

    return other.strategy === this.strategy &&
      other.allowGenericOrders === this.allowGenericOrders &&
      other.shouldSendElectronicPass === this.shouldSendElectronicPass;
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof VouchersOnlyEventBookingProtocol;
  }

  static assertType(order: DataModel): order is VouchersOnlyEventBookingProtocol {
    return order instanceof VouchersOnlyEventBookingProtocol;
  }
}

export class VouchersOnlyEventBookingProtocolBuilder extends DataModelBuilder<VouchersOnlyEventBookingProtocol> {
  build(context: SerializationContext, map: SerializedData): VouchersOnlyEventBookingProtocol {
    return VouchersOnlyEventBookingProtocol.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(VouchersOnlyEventBookingProtocol.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(VouchersOnlyEventBookingProtocol.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the `shouldSendElectronicPass` field of type `boolean` was introduced
    if(version < 2) {
      map['shouldSendElectronicPass'] = true;
      version = 2;
    }

    // In this version we added 'allowGenericOrders' of type 'boolean'.
    if(version < 3) {
      map['allowGenericOrders'] = false;
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
